import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./AddCenter.module.sass";
import { use100vh } from "react-div-100vh";
import { useNavigate } from "react-router-dom";
import TextInput from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import Image from "../../components/Image";
import { useAuth } from "../../contexts/AuthContext";
import { showSuccess, showError } from "../../services/toast.service";
import { pb } from "../../services/pocketbase";
import Switch from "../../components/Switch";

const CompletAccountAddCenter = () => {
  const heightWindow = use100vh();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [center, setCenter] = useState({
    name: '',
    address: '',
    address_complement: '',
    zip_code: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    siret: '',
    uai: '',
    is_company_cfa: false,
    main_cfa_location: true
  });

  const [isCompanyCfa, setIsCompanyCfa] = useState(false);
  const [isMainCfaLocation, setIsMainCfaLocation] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  useEffect(() => {
    if (!pb.authStore.isValid) {
      console.error('Utilisateur non connecté');
      navigate('/signin');
      return;
    }
  }, [navigate]);

  const handleCenterChange = (e) => {
    const { name, value } = e.target;
    setCenter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (name, value) => {
    if (name === 'is_company_cfa') {
      setIsCompanyCfa(value);
      setCenter(prev => ({
        ...prev,
        is_company_cfa: value
      }));
    } else if (name === 'main_cfa_location') {
      setIsMainCfaLocation(value);
      setCenter(prev => ({
        ...prev,
        main_cfa_location: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (!center.name || !center.address || !center.zip_code || !center.city || !center.phone || !center.email) {
        showError('Veuillez remplir tous les champs obligatoires');
        setIsSaving(false);
        return;
      }

      if (!pb.authStore.model.partner_id) {
        showError('Aucun partenaire associé à votre compte');
        setIsSaving(false);
        return;
      }

      const centerData = {
        name: center.name,
        address: center.address,
        address_complement: center.address_complement || '',
        zip_code: center.zip_code,
        city: center.city,
        country: center.country,
        phone: center.phone,
        email: center.email,
        siret: center.siret || '',
        uai: center.uai || '',
        is_company_cfa: isCompanyCfa,
        main_cfa_location: isMainCfaLocation,
        partner_id: pb.authStore.model.partner_id,
        is_active: true
      };

      // Créer le centre
      const result = await pb.collection('centers').create(centerData);
      showSuccess('Centre créé avec succès');

      // Mettre à jour l'état first_connexion de l'utilisateur
      try {
        await pb.collection('users').update(pb.authStore.model.id, {
          first_connexion: false
        });
      } catch (userUpdateError) {
        console.error('Erreur lors de la mise à jour des informations utilisateur:', userUpdateError);
      }

      // Rediriger vers le tableau de bord
      navigate('/admin/dashboard');

    } catch (error) {
      console.error('Erreur lors de la création du centre:', error);
      showError('Erreur lors de la création: ' + (error.message || 'Veuillez vérifier vos informations'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = async () => {
    try {
      await pb.collection('users').update(pb.authStore.model.id, {
        first_connexion: false
      });
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Erreur lors de la mise à jour des informations utilisateur:', error);
      navigate('/admin/dashboard');
    }
  };

  const handlePrevious = () => {
    navigate('/partner/complete-account');
  };

  return (
    <div className={styles.row}>
      <div
        className={styles.col}
        style={{
          backgroundImage: `url('/images/content/loginbg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: heightWindow,
          flex: 1
        }}
      />
      <div className={styles.col} style={{ minHeight: heightWindow, flex: 1.5, overflow: 'auto', maxHeight: '100vh' }}>
        <div className={styles.head}>
          <div className={styles.logo}>
            <Image
              className={styles.pic}
              src="/images/logo.png"
              srcDark="/images/logo.png"
              alt="Core"
            />
          </div>
          <div className={cn("h2", styles.title)}>Créer votre premier centre de formation</div>
        </div>

        <div className={styles.container}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.body}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Informations générales
                </h3>
                  <TextInput
                    className={styles.field}
                    label="Nom du centre"
                    name="name"
                    type="text"
                    placeholder="Nom du centre de formation"
                    value={center.name}
                    onChange={handleCenterChange}
                    required
                  />
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Adresse
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Adresse"
                    name="address"
                    type="text"
                    placeholder="Adresse du centre"
                    value={center.address}
                    onChange={handleCenterChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Complément d'adresse"
                    name="address_complement"
                    type="text"
                    placeholder="Complément d'adresse"
                    value={center.address_complement}
                    onChange={handleCenterChange}
                  />
                  <TextInput
                    className={styles.field}
                    label="Code postal"
                    name="zip_code"
                    type="text"
                    placeholder="Code postal"
                    value={center.zip_code}
                    onChange={handleCenterChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Ville"
                    name="city"
                    type="text"
                    placeholder="Ville"
                    value={center.city}
                    onChange={handleCenterChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Pays"
                    name="country"
                    type="text"
                    placeholder="Pays"
                    value={center.country}
                    onChange={handleCenterChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Coordonnées
                </h3>
                <div className={styles.formGrid}>
                  <PhoneInput
                    className={styles.field}
                    label="Numéro de téléphone"
                    name="phone"
                    value={center.phone}
                    onChange={(phone) => {
                      setCenter(prev => ({
                        ...prev,
                        phone: phone.target.value
                      }));
                    }}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="email@centre.com"
                    value={center.email}
                    onChange={handleCenterChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Informations administratives
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Numéro SIRET"
                    name="siret"
                    type="text"
                    placeholder="Numéro SIRET"
                    value={center.siret}
                    onChange={handleCenterChange}
                  />
                  <TextInput
                    className={styles.field}
                    label="Numéro UAI"
                    name="uai"
                    type="text"
                    placeholder="Numéro UAI"
                    value={center.uai}
                    onChange={handleCenterChange}
                  />
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Type de centre
                </h3>
                <div className={styles.line} style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div className={styles.title} style={{ flex: 1 }}>
                    Centre de formation d'entreprise
                  </div>
                  <Switch
                    className={styles.switch}
                    value={isCompanyCfa}
                    onChange={(value) => handleSwitchChange('is_company_cfa', value)}
                  />
                </div>
                <div className={styles.line} style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                  <div className={styles.title} style={{ flex: 1 }}>
                    Centre de formation principal
                  </div>
                  <Switch
                    className={styles.switch}
                    value={isMainCfaLocation}
                    onChange={(value) => handleSwitchChange('main_cfa_location', value)}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px', paddingBottom: '20px', width: '100%' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    className={cn("button-stroke", styles.buttonStroke)}
                    onClick={handlePrevious}
                  >
                    Précédent
                  </button>
                  <button
                    type="button"
                    className={cn("button-stroke", styles.buttonStroke)}
                    onClick={handleSkip}
                  >
                    Ignorer cette étape
                  </button>
                </div>
                <div>
                  <button
                    className={cn("button", styles.button)}
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? "Création en cours..." : "Terminer"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompletAccountAddCenter;
